import { useEffect, useMemo } from 'react';
import {
  ProjectSettingsAccordion,
  ProjectMetricThresholdSection,
  useMetricThresholdDrafts,
  useProjectByIdQuery,
  useUpdateProject,
} from '@/features/projects';
import {
  defaultProjectMetricThresholds,
  mergeProjectThresholdGroup,
} from '@/features/projects/utils/projectMetricThresholds';
import { useNotifications } from '@/providers/notificationHooks';
import { useMutationErrorSnackbar } from '@/shared/hooks';
import { Text } from '@/ui';
import type { ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type PostOnboardingTutorialThresholdSectionProps = {
  projectId: string | null;
};

type ThresholdGroupConfig = {
  title: string;
  description: string;
  successMessage: string;
  thresholdKey: 'executionRuns' | 'suites' | 'tests';
};

const LoadedThresholdGroupSection = ({
  config,
  project,
}: {
  config: ThresholdGroupConfig;
  project: ProjectDto;
}): ReactElement => {
  const { notify } = useNotifications();
  const mutationError = useMutationErrorSnackbar();
  const updateProject = useUpdateProject();
  const thresholdSource = useMemo(
    () =>
      mergeProjectThresholdGroup(
        defaultProjectMetricThresholds[config.thresholdKey],
        project.metricThresholds[config.thresholdKey],
      ),
    [config.thresholdKey, project],
  );
  const { drafts, setDrafts, preferredUnitsRef, resetDrafts, isValid } =
    useMetricThresholdDrafts(thresholdSource);

  useEffect(() => {
    resetDrafts(thresholdSource);
  }, [resetDrafts, thresholdSource]);

  return (
    <ProjectMetricThresholdSection
      title={config.title}
      description={config.description}
      successMessage={config.successMessage}
      thresholdKey={config.thresholdKey}
      drafts={drafts}
      setDrafts={setDrafts}
      preferredUnitsRef={preferredUnitsRef}
      isValid={isValid}
      isPending={updateProject.isPending}
      gridSpacing="sm"
      project={project}
      updateProject={updateProject}
      mutationError={mutationError}
      notify={notify}
      resetDrafts={resetDrafts}
      container="none"
    />
  );
};

const ThresholdGroupSection = ({
  config,
  projectId,
}: {
  config: ThresholdGroupConfig;
  projectId: string;
}): ReactElement => {
  const projectQuery = useProjectByIdQuery(projectId);
  const project = projectQuery.data ?? null;

  if (projectQuery.isPending && project === null) {
    return <Text role="body">Loading thresholds…</Text>;
  }

  if (project === null) {
    return <Text role="body">Thresholds are unavailable right now.</Text>;
  }

  return <LoadedThresholdGroupSection config={config} project={project} />;
};

export const PostOnboardingTutorialThresholdSection = ({
  projectId,
}: PostOnboardingTutorialThresholdSectionProps): ReactElement | null => {
  if (projectId === null) {
    return null;
  }

  return (
    <>
      <ProjectSettingsAccordion
        title="Execution Runs"
        description="Set target and warning thresholds for execution run metrics."
        defaultExpanded={false}
        preloadContent={true}
        transitionMode="fade"
      >
        <ThresholdGroupSection
          config={{
            title: 'Execution Run Metric Thresholds',
            description: 'Set target and warning thresholds for execution run metrics.',
            successMessage: 'Saved execution run metric thresholds',
            thresholdKey: 'executionRuns',
          }}
          projectId={projectId}
        />
      </ProjectSettingsAccordion>
      <ProjectSettingsAccordion
        title="Suites"
        description="Set target and warning thresholds for suite-level quality metrics."
        preloadContent={true}
        transitionMode="fade"
      >
        <ThresholdGroupSection
          config={{
            title: 'Suite Metric Thresholds',
            description: 'Set target and warning thresholds for suite-level quality metrics.',
            successMessage: 'Saved suite metric thresholds',
            thresholdKey: 'suites',
          }}
          projectId={projectId}
        />
      </ProjectSettingsAccordion>
      <ProjectSettingsAccordion
        title="Tests"
        description="Set target and warning thresholds for test-level quality metrics."
        preloadContent={true}
        transitionMode="fade"
      >
        <ThresholdGroupSection
          config={{
            title: 'Test Metric Thresholds',
            description: 'Set target and warning thresholds for test-level quality metrics.',
            successMessage: 'Saved test metric thresholds',
            thresholdKey: 'tests',
          }}
          projectId={projectId}
        />
      </ProjectSettingsAccordion>
    </>
  );
};
